import { SupabaseClient } from "@supabase/supabase-js";
import { readProvedLink } from "@/lib/auth/linked-identity";

/** A member of staff, resolved from their login. */
export interface LinkedAdmin {
  /** The person's `nowadays.users.user_id`. */
  userId: number;
  /** The address stored on the person's account, not the session's email claim. */
  email: string;
  /** True when any of the person's `nowadays.admins` rows has `is_va` set. `admins.email` is not
   *  unique, so a person can hold more than one row; one VA row makes them a VA. */
  isVa: boolean;
}

/** A staff record that also carries the ops flag, for analytics segmentation. */
export interface LinkedAdminWithOps extends LinkedAdmin {
  /** True when any of the person's active `nowadays.admins` rows has `ops` set. */
  ops: boolean;
}

/** A link row with the linked person and their admin rows embedded. */
interface LinkedAdminRow {
  user_id: number;
  users: { email: string; admins: { is_va: boolean }[] } | null;
}

/**
 * The staff record of the person a login is linked to.
 *
 * Joins through `nowadays.user_auth_identity`, never the address. Matching the
 * session's email against `nowadays.admins.email` meant that signing up with a
 * staff address made you staff (ENG-726).
 *
 * @param supabase - A session client for the same login (RLS shows the row only
 *   to staff), or a service-role client.
 * @param login - The verified Supabase user. Pass the user object, not an id
 *   or an address, so a call site cannot hand it the email claim by mistake.
 * @returns The admin, or null when the login is unlinked or not staff.
 * @throws An error if there is an issue fetching the admin
 */
export async function getLinkedAdmin(supabase: SupabaseClient, login: { id: string }): Promise<LinkedAdmin | null> {
  const data = await readProvedLink<LinkedAdminRow>(
    supabase,
    login.id,
    "user_id, users!inner(email, admins!inner(is_va))"
  ).catch((error: Error) => {
    throw new Error(`Error fetching nowadays admin: ${error.message}`);
  });

  const admins = data?.users?.admins ?? [];
  if (!data?.users || admins.length === 0) return null;

  return { userId: data.user_id, email: data.users.email, isVa: admins.some((row) => row.is_va) };
}

/**
 * Checks if a login belongs to a Nowadays admin.
 *
 * @param supabase - A session client for the same login, or a service-role client.
 * @param login - The verified Supabase user (`auth.getUser()`), not its email.
 * @returns True if the login is linked to a person in `nowadays.admins`
 * @throws An error if there is an issue fetching the admin
 */
export async function isNowadaysAdmin(supabase: SupabaseClient, login: { id: string }): Promise<boolean> {
  return (await getLinkedAdmin(supabase, login)) !== null;
}

/**
 * Checks if the caller of a session client is a Nowadays admin, asking the
 * database's own `is_member_of_admins()`, which resolves the caller from the
 * session's login through its link.
 *
 * Needs no login id, so it can run in parallel with `auth.getUser()` or
 * `getCurrentUser` instead of after them.
 *
 * @param sessionClient - A session client for the caller. A service-role client
 *   has no caller and always gets false.
 * @returns True if the caller's linked person has a row in `nowadays.admins`
 * @throws An error if the check fails
 */
export async function isCallerNowadaysAdmin(sessionClient: SupabaseClient): Promise<boolean> {
  const { data, error } = await sessionClient.schema("nowadays").rpc("is_member_of_admins", {});

  if (error) {
    throw new Error(`Error checking nowadays admin: ${error.message}`);
  }

  return data === true;
}

/**
 * Checks if a person is a Nowadays admin, by their `nowadays.users.user_id`.
 *
 * For callers that already hold a resolved person (for example from
 * `getCurrentUser`) rather than the login.
 *
 * @param supabase - The Supabase client
 * @param userId - The person's `nowadays.users.user_id`
 * @returns True if the person has a row in `nowadays.admins`
 * @throws An error if there is an issue fetching the admin
 */
export async function isNowadaysAdminUserId(supabase: SupabaseClient, userId: number): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("admins")
    .select("user_id")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    throw new Error(`Error fetching nowadays admin: ${error.message}`);
  }

  return !!data;
}

/**
 * Whether a person is on the ops team, by their `nowadays.users.user_id`.
 *
 * Narrower than {@link isNowadaysAdminUserId} in the two ways that decide
 * authority. It wants the `ops` flag, because engineers and VAs hold admins
 * rows too and answering for a client's event is not their job. And it wants
 * `is_active`, because somebody taken off the roster should stop acting on
 * every client's events on the day, not whenever the row is deleted.
 *
 * Fails closed and never throws: it only ever widens what a caller may do, so
 * a transient read error should leave the ordinary grants to decide rather
 * than turn a client's own send into an error.
 *
 * Its twin is `PlannerStaffStore.roster` in the backend repo
 * (`apps/backend/src/planner-bot/access/planner-staff.store.ts`),
 * which derives the bot's READ scope from this same column. The two must agree
 * on what "ops" means (them disagreeing is what made the bot offer a draft it
 * could not send), so a change to either predicate belongs in both.
 *
 * @param supabase - The Supabase client (service role: there is no session here)
 * @param userId - The person's `nowadays.users.user_id`
 * @returns Whether that person has an active ops row
 */
export async function isOpsTeamUserId(supabase: SupabaseClient, userId: number): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("admins")
    .select("user_id")
    .eq("user_id", userId)
    .eq("ops", true)
    .eq("is_active", true)
    // `admins` has an index on `user_id` but no unique constraint, so a second
    // row would make `maybeSingle` an error and fail ops closed. The backend's
    // twin takes the first row for the same reason.
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("isOpsTeamUserId: roster read failed", { userId }, error);
    return false;
  }

  return !!data;
}

/** How much staff authority a person's active `nowadays.admins` rows give them. */
export type StaffTier = "staff" | "va";

/**
 * A person's staff tier, by their `nowadays.users.user_id`.
 *
 * Reads every active row, because `admins.user_id` is indexed but not unique.
 * Any VA row makes the answer `"va"`, so a person holding both a VA row and a
 * staff row is treated as the narrower of the two (fail closed). An `is_va`
 * that is anything but `false` counts as a VA row for the same reason.
 *
 * @param supabase - The Supabase client (service role: there is no session here)
 * @param userId - The person's `nowadays.users.user_id`
 * @returns `"staff"`, `"va"`, or null when the person has no active row
 * @throws An error if the roster read fails, so the caller can answer 500
 *   rather than mistake an outage for "not staff"
 */
export async function getStaffTier(supabase: SupabaseClient, userId: number): Promise<StaffTier | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("admins")
    .select("is_va")
    .eq("user_id", userId)
    .eq("is_active", true);

  if (error) {
    throw new Error(`Error fetching staff tier: ${error.message}`);
  }

  const rows = (data ?? []) as { is_va: boolean | null }[];
  if (rows.length === 0) return null;
  return rows.some((row) => row.is_va !== false) ? "va" : "staff";
}

/**
 * Checks if a typed address belongs to a Nowadays admin.
 *
 * Only for decisions about an address somebody typed before any session exists,
 * such as refusing an email code to a staff address at login. It must never
 * decide who a signed-in caller is: use {@link isNowadaysAdmin} for that.
 *
 * @param supabase - The Supabase client (service role, since there is no session)
 * @param email - The typed address
 * @returns True if the address is in `nowadays.admins`
 * @throws An error if there is an issue fetching the admin
 */
export async function isNowadaysAdminEmail(supabase: SupabaseClient, email: string): Promise<boolean> {
  const { data, error } = await supabase.schema("nowadays").from("admins").select("*").eq("email", email).maybeSingle();

  if (error) {
    throw new Error(`Error fetching nowadays admin: ${error.message}`);
  }

  return !!data;
}
